import React from 'react';
import Modal from './Modal';
import OptimizationFailureCard from './OptimizationFailureCard';

interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  errorLog?: string;
  type?: 'error' | 'warning' | 'info' | 'success';
}

export function AlertModal({ isOpen, onClose, title, message, errorLog, type = 'error' }: AlertModalProps) {
  const getIcon = () => {
    switch (type) {
      case 'error':
        return (
          <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-12 h-12 text-yellow-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'success':
        return (
          <svg className="w-12 h-12 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-12 h-12 text-blue-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getTitleColor = () => {
    switch (type) {
      case 'error':
        return 'text-red-600';
      case 'warning':
        return 'text-yellow-600';
      case 'success':
        return 'text-green-600';
      case 'info':
      default:
        return 'text-blue-600';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="border-2 border-black bg-white p-6 max-w-md">
        <div className="text-center">
          {getIcon()}
          <h3 className={`text-xl font-semibold mb-3 ${getTitleColor()}`}>
            {title}
          </h3>
          {errorLog && errorLog.trim().length > 0 && (
            <OptimizationFailureCard errorLog={errorLog} className="mb-4 text-left" />
          )}
          <p className="text-gray-700 mb-6 whitespace-pre-line">
            {message}
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 border-2 border-black bg-white text-black font-medium hover:bg-gray-100 transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default AlertModal;